// ElevenLabs voice-agent widget, loaded on demand.
//
// Nothing ElevenLabs-hosted may load before a .js-elevenlabs-trigger is clicked: the
// embed is ~1.5 MB of JS, and once it runs it calls the agent API and fetches flag
// images even while the widget is hidden. The click listener is delegated on document,
// so it works no matter when this file executes relative to DOMContentLoaded.
//
// Trigger contract (lives in merchant page content):
//   <button class="js-elevenlabs-trigger" data-agent="agent_..." data-first-message="...">
(() => {
    const EMBED_SRC = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
    const TAG = 'elevenlabs-convai';
    const LOAD_TIMEOUT_MS = 15000;
    const MOBILE_MAX_WIDTH = 640;

    let embedScript = null;
    let embedUnusable = null;
    let widget = null;
    let opening = false;

    function loadEmbed() {
        if (customElements.get(TAG)) return Promise.resolve();
        if (embedUnusable) return Promise.reject(embedUnusable);

        if (!embedScript) {
            embedScript = document.createElement('script');
            embedScript.src = EMBED_SRC;
            embedScript.async = true;
            document.head.appendChild(embedScript);
        }
        const script = embedScript;

        return new Promise((resolve, reject) => {
            // A timed-out script stays in place: it may still arrive, and injecting a
            // second copy would make the embed call customElements.define twice.
            const timer = setTimeout(() => {
                reject(new Error(`timed out after ${LOAD_TIMEOUT_MS}ms loading ${EMBED_SRC}`));
            }, LOAD_TIMEOUT_MS);

            customElements.whenDefined(TAG).then(() => {
                clearTimeout(timer);
                resolve();
            });

            script.addEventListener('error', () => {
                clearTimeout(timer);
                // A script that failed to fetch never executes, so a fresh tag can retry.
                if (embedScript === script) {
                    script.remove();
                    embedScript = null;
                }
                reject(new Error(`network error loading ${EMBED_SRC}`));
            }, { once: true });

            script.addEventListener('load', () => {
                if (customElements.get(TAG)) return;
                clearTimeout(timer);
                embedUnusable = new Error(`${EMBED_SRC} loaded but did not register <${TAG}>`);
                reject(embedUnusable);
            }, { once: true });
        });
    }

    function updateWidgetVariant() {
        const isMobile = window.innerWidth <= MOBILE_MAX_WIDTH;
        widget.setAttribute('variant', isMobile ? 'expandable' : 'full');
    }

    function showWidget(agentId, firstMessage) {
        const wrapper = document.createElement('div');
        wrapper.className = 'elevenlabs-widget-wrapper';

        widget = document.createElement(TAG);
        widget.setAttribute('agent-id', agentId);
        if (firstMessage) widget.setAttribute('override-first-message', firstMessage);
        updateWidgetVariant();
        window.addEventListener('resize', updateWidgetVariant);

        wrapper.appendChild(widget);
        document.body.appendChild(wrapper);

        // Hide the support chat only once the voice widget is really on screen, so a
        // failed load never leaves the visitor with neither.
        if (window.theme && theme.ChatWidget) theme.ChatWidget.hide();
        document.body.classList.add('elevenlabs-widget-enabled');
    }

    function open(trigger) {
        if (widget || opening) return;

        const agentId = (trigger.dataset.agent || '').trim();
        if (!agentId) {
            console.error('[ElevenLabs] .js-elevenlabs-trigger is missing data-agent; widget not loaded.');
            return;
        }
        const firstMessage = (trigger.dataset.firstMessage || '').trim();

        opening = true;
        trigger.setAttribute('aria-busy', 'true');

        loadEmbed()
            .then(() => showWidget(agentId, firstMessage))
            .catch((error) => {
                console.error('[ElevenLabs] Could not open the voice widget:', error);
            })
            .finally(() => {
                opening = false;
                trigger.removeAttribute('aria-busy');
            });
    }

    document.addEventListener('click', (event) => {
        const trigger = event.target instanceof Element && event.target.closest('.js-elevenlabs-trigger');
        if (trigger) open(trigger);
    });
})();
